import {
    Box,
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ChatTitle from '../ChatTitle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Locale from '/b2b/common/components/Locale';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonIcon from '@mui/icons-material/PersonOutline';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import styles from './TopBar.styles';
import { translate } from '/b2b/common/helpers/i18n';

const useStyles = makeStyles()(styles);

/*
 * Fullscreen-mode header: Chats menu, New-conversation button, centered title with an
 * adjacent ellipsis actions menu, and the Escalate action. Presentational only —
 * behavior comes from the shell via callbacks. Other panel modes (sidebar/widget)
 * provide their own headers.
 */
const TopBar = props => {
    const {
        activeConversationId,
        title,
        canEscalate,
        onOpenChats,
        onNewConversation,
        onEscalate,
        onDelete,
    } = props;
    const { classes } = useStyles(undefined, {
        props,
    });

    const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
    const actionsMenuOpen = Boolean(actionsAnchorEl);
    const openActionsMenu = event => setActionsAnchorEl(event.currentTarget);
    const closeActionsMenu = () => setActionsAnchorEl(null);
    const selectAction = action => {
        closeActionsMenu();
        if (action) action();
    };

    return (
        <Box className={classes.topBar}>
            <Box className={classes.topBarLeft}>
                <Button
                    className={classes.grayButton}
                    variant="outlined"
                    size="small"
                    startIcon={<MenuIcon />}
                    onClick={onOpenChats}
                >
                    <Locale path="aiAssistant.buttons.chats.label" />
                </Button>
                {activeConversationId && (
                    <Button
                        className={classes.grayButton}
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onNewConversation}
                        aria-label={translate('aiAssistant.buttons.newChat.ariaLabel')}
                    >
                        <Locale path="common.new" />
                    </Button>
                )}
            </Box>
            <Box className={classes.topBarContent}>
                {title && <ChatTitle title={title} className={classes.topBarTitle} />}
                {activeConversationId && (
                    <>
                        <Tooltip title={translate('aiAssistant.buttons.chatActions.ariaLabel')}>
                            <IconButton
                                className={classes.titleActions}
                                onClick={openActionsMenu}
                                size="small"
                                aria-label={translate('aiAssistant.buttons.chatActions.ariaLabel')}
                                aria-haspopup="true"
                                aria-expanded={actionsMenuOpen}
                            >
                                <MoreHorizIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={actionsAnchorEl}
                            open={actionsMenuOpen}
                            onClose={closeActionsMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem
                                onClick={() => selectAction(onDelete)}
                                sx={{ color: 'error.main' }}
                            >
                                <ListItemIcon>
                                    <DeleteOutlineIcon fontSize="small" color="error" />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ color: 'error.main' }}>
                                    {translate('aiAssistant.buttons.deleteChat.label')}
                                </ListItemText>
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>
            {canEscalate && (
                <Box className={classes.topBarRight}>
                    <Button
                        className={classes.grayButton}
                        variant="outlined"
                        size="small"
                        startIcon={<PersonIcon />}
                        onClick={onEscalate}
                    >
                        <Locale path="aiAssistant.buttons.escalate.label" />
                    </Button>
                </Box>
            )}
        </Box>
    );
};

TopBar.propTypes = {
    activeConversationId: PropTypes.string,
    title: PropTypes.string,
    canEscalate: PropTypes.bool,
    onOpenChats: PropTypes.func.isRequired,
    onNewConversation: PropTypes.func.isRequired,
    onEscalate: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
};

export default TopBar;
